import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Grid, Icon, Popover, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import CheckboxGroupFilter from 'components/Filters/FilterPanel/subcomponents/CheckboxGroupFilter'
import subbuttonStyles from 'components/Filters/FilterPanel/subcomponents/SubButtons/subbuttons.module.scss'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'
import styles from 'components/Filters/FilterPanel/subcomponents/CheckboxSelector/checkbox-selector.module.scss'
import clsx from 'clsx'
import isObject from 'lodash/isObject'

const CheckboxSelector = ({
  filterManager,
  hasBackdrop = false,
  handleHide = () => {},
  onApply = (value) => {
    console.log(value)
  },
  popoverConfig = {
    variant: 'popover',
    id: 'checkbox-Selector-Popover',
    breakpoints: {
      0: {
        variant: 'bottomSheet',
        hasBackdrop: true,
        showLabel: true,
      },
      960: {
        variant: 'popover',
        hasBackdrop: false,
      },
    },
  },
  value = [],
  label = '',
  name,
  icon,
  className = '',
  ...rest
}) => {
  const { t } = useTranslation()
  const [checked, setChecked] = useState(value)
  const { filters, configuration } = filterManager

  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const popoverState = usePopupState(popoverConfig)

  useEffect(() => !isEqual(checked, value) && setChecked(value), [value])

  const applySelector = (option) => {
    onApply(isObject(option) ? option.value : option)
    popoverState.close()
  }

  const handleClose = () => {
    handleHide()
    popoverState.close()
  }

  const selectedValues = useMemo(() => {
    if (Array.isArray(filters?.[name])) {
      if (filters?.[name]?.length > 0) {
        return filters?.[name].map((val) =>
          configuration[name].options.find((option) =>
            isObject(option) ? option.value === val : option === val,
          ),
        )
      }
    } else {
      if (filters?.[name] !== undefined && filters?.[name] !== '') {
        return [filters[name]]
      }
    }
    return []
  }, [filters, t])

  return (
    <>
      <Button
        className={clsx(styles['button'], className, {
          [styles['button_opened']]: popoverState.isOpen,
          ['padding-top-8 padding-bottom-8']: selectedValues.length === 0,
        })}
        variant={'ghost'}
        grey
        size={'l'}
        {...bindTrigger(popoverState)}
        startIcon={icon}
        endIcon={<Icon name="arrow-down" />}
      >
        <Grid
          direction={'column'}
          gap={2}
          alignItems={'start'}
          justifyContent={'start'}
        >
          <Typography
            variant={selectedValues.length ? 'Body-4' : 'Body-Forms-20'}
            color={'Label-Secondary'}
          >
            {t(label)}
          </Typography>
          <Grid
            direction={'row'}
            gap={4}
            wrap={false}
          >
            {selectedValues.length > 0 && (
              <Badge
                size={'xs'}
                color={'Label-Fifth'}
                bgColor={'Background-Six'}
                noWrap
                className={styles['button__badge']}
              >
                {isObject(selectedValues[0])
                  ? selectedValues[0].label
                  : t((configuration[name]?.optionLabelPrefix ?? '') + selectedValues[0])}
              </Badge>
            )}
            {selectedValues.length > 1 && (
              <Badge
                size={'xs'}
                color={'Label-Fifth'}
                bgColor={'Background-Six'}
              >
                + {selectedValues.length - 1}
              </Badge>
            )}
          </Grid>
        </Grid>
      </Button>

      <Popover
        hasBackdrop={hasBackdrop}
        bodyClassName={subbuttonStyles['subbuttons-filter__popover-inner']}
        {...bindPopover(popoverState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={handleClose}
      >
        <CheckboxGroupFilter
          {...rest}
          name={name}
          label={label}
          icon={icon}
          filterManager={filterManager}
          onApply={applySelector}
          value={checked}
        />
      </Popover>
    </>
  )
}

CheckboxSelector.propTypes = {
  onApply: PropTypes.func,
  popoverConfig: PropTypes.object,
  hasBackdrop: PropTypes.bool,
  handleHide: PropTypes.func,
  value: PropTypes.array,
  filterManager: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.element,
  className: PropTypes.string,
}

export default CheckboxSelector
